import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const API_URL = import.meta.env.MODE === 'production' ? '/api' : (import.meta.env.VITE_API_URL || '/api')

function getCsrfToken() {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === 'csrfToken') return value
    }
    return null
}

async function apiRequest(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json' }
    const method = options.method || 'GET'
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
        const csrf = getCsrfToken()
        if (csrf) headers['X-CSRF-Token'] = csrf
    }
    const res = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: { ...headers, ...options.headers },
        credentials: 'include',
    })
    if (!res.ok) return null
    if (res.status === 204) return null
    return res.json()
}

function formatTime(ts) {
    if (!ts) return ''
    const d = new Date(ts)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function SupportChatWidgetInner({ user }) {
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const [chat, setChat] = useState(null)          // { id, status, messages[] }
    const [messages, setMessages] = useState([])
    const [input, setInput] = useState('')
    const [sending, setSending] = useState(false)
    const [unread, setUnread] = useState(0)
    const seenIds = useRef(new Set())
    const bottomRef = useRef(null)
    const textareaRef = useRef(null)

    // Load widget chat on mount
    useEffect(() => {
        apiRequest('/support/widget').then(data => {
            if (!data) return
            setChat(data)
            setMessages(data.messages || [])
            data.messages?.forEach(m => seenIds.current.add(m.id))
            // Count unread admin messages (those after user_last_read_at — we approximate
            // by checking sender_role since we don't have that timestamp in state yet)
            const unreadCount = (data.messages || []).filter(
                m => m.sender_role === 'admin'
            ).length
            // We'll refine this after mark-read; for now show 0 on first load
            // since the user can see the messages on the page they just loaded
        })
    }, [user?.id])

    // Listen for real-time messages via window event (dispatched by AuthContext pattern,
    // but we use our own 'support:message' event that we register here)
    useEffect(() => {
        const handler = (e) => {
            const { chatId, message } = e.detail || {}
            if (!chat?.id || String(chatId) !== String(chat.id)) return
            if (seenIds.current.has(message.id)) return
            seenIds.current.add(message.id)

            // If this is our own message echoing back over the socket, the
            // optimistic temp_ entry is still in the list (POST hasn't returned
            // yet, or arrived after this socket event). Swap the temp for the
            // real message instead of appending — otherwise we'd render twice.
            if (message.sender_role === 'user') {
                setMessages(prev => {
                    const tempIdx = prev.findIndex(m =>
                        typeof m.id === 'string' && m.id.startsWith('temp_') && m.body === message.body
                    )
                    if (tempIdx >= 0) {
                        const next = prev.slice()
                        next[tempIdx] = message
                        return next
                    }
                    return [...prev, message]
                })
                return
            }

            setMessages(prev => [...prev, message])
            if (message.sender_role === 'admin') {
                if (!open) {
                    setUnread(n => n + 1)
                    // Subtle sound
                    try {
                        const ctx = new (window.AudioContext || window.webkitAudioContext)()
                        const osc = ctx.createOscillator()
                        const gain = ctx.createGain()
                        osc.connect(gain)
                        gain.connect(ctx.destination)
                        osc.frequency.value = 520
                        gain.gain.setValueAtTime(0.15, ctx.currentTime)
                        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4)
                        osc.start(ctx.currentTime)
                        osc.stop(ctx.currentTime + 0.4)
                    } catch (_) {}
                }
            }
        }
        window.addEventListener('support:message', handler)
        return () => window.removeEventListener('support:message', handler)
    }, [chat?.id, open])

    // Also register the Socket.IO event to forward into the window event system.
    // AuthContext already holds the socket but doesn't expose it. We listen to
    // the socket indirectly: the server emits 'support:message' to user_${id} room,
    // which the AuthContext socket receives. We add a listener here via the socket
    // stored on the window (a convention used by the existing chat pages).
    useEffect(() => {
        // Poll for socket availability (it's set up asynchronously in AuthContext)
        let intervalId
        const tryAttach = () => {
            // AuthContext doesn't expose socket directly, so we hook into the
            // io client that was created. We look for it on the window object
            // as a fallback. The real path: re-export socket from AuthContext.
            // For now we rely on the custom event we dispatch from the socket listener
            // added in a sibling effect below.
        }
        intervalId = setInterval(tryAttach, 500)
        return () => clearInterval(intervalId)
    }, [])

    // Scroll to bottom on new messages
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    // Mark as read when panel opens
    useEffect(() => {
        if (open && chat?.id) {
            setUnread(0)
            apiRequest('/support/widget/read', { method: 'PATCH' }).catch(() => {})
        }
    }, [open, chat?.id])

    const sendMessage = useCallback(async () => {
        const body = input.trim()
        if (!body || sending) return
        setSending(true)

        // Optimistic insert
        const tempId = `temp_${Date.now()}`
        const optimistic = {
            id: tempId,
            sender_role: 'user',
            body,
            created_at: new Date().toISOString(),
        }
        seenIds.current.add(tempId)
        setMessages(prev => [...prev, optimistic])
        setInput('')

        try {
            const msg = await apiRequest('/support/widget/messages', {
                method: 'POST',
                body: JSON.stringify({ body }),
            })
            if (msg) {
                // Replace optimistic with real message — but if the socket
                // already inserted the real one, just drop the temp to avoid
                // a duplicate.
                seenIds.current.add(msg.id)
                setMessages(prev => {
                    if (prev.some(m => m.id === msg.id)) {
                        return prev.filter(m => m.id !== tempId)
                    }
                    return prev.map(m => m.id === tempId ? msg : m)
                })
                if (!chat) {
                    // First message created the chat; reload to get id
                    const fresh = await apiRequest('/support/widget')
                    if (fresh) {
                        setChat(fresh)
                        setMessages(fresh.messages || [])
                    }
                }
            }
        } catch (_) {
            // Remove optimistic on failure
            setMessages(prev => prev.filter(m => m.id !== tempId))
            setInput(body)
        } finally {
            setSending(false)
        }
    }, [input, sending, chat])

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    const badgeCount = unread > 9 ? '9+' : unread
    const isDashboard = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/admin')

    return (
        <div className={`support-chat-widget${isDashboard ? ' support-chat-widget--dashboard' : ''}`}>
            {/* Chat panel */}
            {open && (
                <div
                    style={{
                        width: '340px',
                        height: '460px',
                        marginBottom: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        overflow: 'hidden',
                        background: 'var(--card-bg, #fff)',
                        border: '1px solid var(--border-color, #e5e7eb)',
                        fontFamily: 'inherit',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: '14px 16px',
                            background: 'var(--accent, #6366f1)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexShrink: 0,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '18px' }}>💬</span>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '14px' }}>Support</div>
                                <div style={{ fontSize: '11px', opacity: 0.85 }}>We typically reply within a few hours</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '18px',
                                lineHeight: 1,
                                padding: '4px',
                                opacity: 0.8,
                            }}
                            aria-label="Close chat"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Messages */}
                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '12px 14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        {messages.length === 0 && (
                            <div
                                style={{
                                    margin: 'auto',
                                    textAlign: 'center',
                                    color: 'var(--muted, #9ca3af)',
                                    fontSize: '13px',
                                    padding: '20px',
                                }}
                            >
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>👋</div>
                                Hi! Send us a message and we'll get back to you shortly.
                            </div>
                        )}

                        {messages.map(m => (
                            <div
                                key={m.id}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: m.sender_role === 'user' ? 'flex-end' : 'flex-start',
                                }}
                            >
                                <div
                                    style={{
                                        maxWidth: '80%',
                                        padding: '8px 12px',
                                        borderRadius: m.sender_role === 'user'
                                            ? '14px 14px 4px 14px'
                                            : '14px 14px 14px 4px',
                                        background: m.sender_role === 'user'
                                            ? 'var(--accent, #6366f1)'
                                            : 'var(--surface-2, #f3f4f6)',
                                        color: m.sender_role === 'user'
                                            ? '#fff'
                                            : 'var(--text, #111827)',
                                        fontSize: '13px',
                                        lineHeight: '1.45',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {m.body}
                                </div>
                                <span style={{ fontSize: '10px', color: 'var(--muted, #9ca3af)', marginTop: '2px' }}>
                                    {m.sender_role === 'admin' ? 'Support · ' : ''}{formatTime(m.created_at)}
                                </span>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    {/* Input */}
                    <div
                        style={{
                            padding: '10px 12px',
                            borderTop: '1px solid var(--border-color, #e5e7eb)',
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'flex-end',
                            flexShrink: 0,
                            background: 'var(--card-bg, #fff)',
                        }}
                    >
                        <textarea
                            ref={textareaRef}
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Type a message… (Enter to send)"
                            rows={1}
                            style={{
                                flex: 1,
                                resize: 'none',
                                border: '1px solid var(--border-color, #e5e7eb)',
                                borderRadius: '8px',
                                padding: '8px 10px',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                outline: 'none',
                                background: 'var(--input-bg, #f9fafb)',
                                color: 'var(--text, #111827)',
                                lineHeight: '1.4',
                                maxHeight: '100px',
                                overflowY: 'auto',
                            }}
                            onInput={e => {
                                e.target.style.height = 'auto'
                                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'
                            }}
                            disabled={sending}
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!input.trim() || sending}
                            style={{
                                background: 'var(--accent, #6366f1)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                width: '36px',
                                height: '36px',
                                cursor: !input.trim() || sending ? 'not-allowed' : 'pointer',
                                opacity: !input.trim() || sending ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '16px',
                            }}
                            aria-label="Send message"
                        >
                            ➤
                        </button>
                    </div>
                </div>
            )}

            {/* Toggle button */}
            <button
                onClick={() => setOpen(o => !o)}
                aria-label={open ? 'Close support chat' : 'Open support chat'}
                style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'var(--accent, #6366f1)',
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    position: 'relative',
                    transition: 'transform 0.15s ease',
                }}
            >
                {open ? '✕' : '💬'}

                {/* Unread badge */}
                {!open && unread > 0 && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            background: '#ef4444',
                            color: '#fff',
                            borderRadius: '50%',
                            minWidth: '20px',
                            height: '20px',
                            fontSize: '11px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 4px',
                            border: '2px solid #fff',
                        }}
                    >
                        {badgeCount}
                    </span>
                )}
            </button>
        </div>
    )
}

export default function SupportChatWidget() {
    const { user } = useAuth()
    if (!user || user.role === 'admin') return null
    return <SupportChatWidgetInner user={user} />
}
